import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, Button, LoadingState, ErrorState, Logo } from "@/components/design-system";
import { mockSessionPublic, mockFinalize, formatCents } from "@/lib/payments";
import { formatApiError } from "@/lib/api";

export default function MockCheckout() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState({ status: "loading", err: "" });
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await mockSessionPublic(sessionId);
        setSession(s);
        setState({ status: "ready", err: "" });
      } catch (err) {
        setState({ status: "error", err: formatApiError(err) });
      }
    })();
  }, [sessionId]);

  const finalize = async (outcome) => {
    setBusy(outcome);
    try {
      await mockFinalize(sessionId, outcome);
      const targetUrl = outcome === "paid" ? session.success_url : session.cancel_url;
      // If success_url points at our own frontend, use react-router to navigate;
      // otherwise fall back to a hard redirect.
      const local = targetUrl?.startsWith(window.location.origin);
      if (local) {
        nav(targetUrl.replace(window.location.origin, ""));
      } else {
        window.location.assign(targetUrl);
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(null);
    }
  };

  if (state.status === "loading") return <LoadingState label="Loading mock checkout…" />;
  if (state.status === "error") return <ErrorState detail={state.err} />;

  return (
    <div className="min-h-screen bg-[#E8F1FF] flex items-center justify-center p-6">
      <div className="w-full max-w-lg space-y-4">
        <div className="flex items-center justify-center gap-3">
          <Logo variant="light" height={40} />
          <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">Mock sandbox</span>
        </div>
        <Card data-testid="mock-checkout" className="!p-8">
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Simulated checkout</p>
          <h1 className="font-[Montserrat] font-extrabold text-3xl text-[#0B1F3A] mt-2">Confirm your subscription</h1>
          <div className="mt-6 rounded-xl bg-[#0B1F3A] text-white p-6">
            <div className="text-xs uppercase tracking-widest text-white/60">Amount due</div>
            <div className="font-[Montserrat] font-extrabold text-5xl mt-1" data-testid="mock-checkout-amount">
              {formatCents(session.amount, session.currency)}
            </div>
            <div className="mt-2 text-white/75 text-sm">
              Recurring {session.interval === "year" ? "annually" : "monthly"} · {session.user_email}
            </div>
          </div>
          <p className="text-xs text-[#0B1F3A]/70 mt-4">
            No real card is captured. Click <b>Pay</b> below to simulate a successful payment webhook,
            or <b>Cancel</b> to exit without charging.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <Button
              variant="secondary"
              onClick={() => finalize("cancel")}
              disabled={!!busy}
              data-testid="mock-checkout-cancel"
            >
              {busy === "cancel" ? "Cancelling…" : "Cancel"}
            </Button>
            <Button
              variant="primary"
              onClick={() => finalize("paid")}
              disabled={!!busy || session.payment_status === "paid"}
              data-testid="mock-checkout-pay"
            >
              {busy === "paid" ? "Paying…" : session.payment_status === "paid" ? "Already paid" : `Pay ${formatCents(session.amount)}`}
            </Button>
          </div>
        </Card>
        <div className="text-center text-xs text-[#0B1F3A]/50 font-mono">{sessionId}</div>
      </div>
    </div>
  );
}
